import { Box, Chip, Divider, Typography, useMediaQuery } from '@mui/material';
import { colors } from '../design/tokens';
import { isCapabilityAvailable } from '../config/capabilities';

const lightColors = {
  bg: '#f8fafc',
  surface: '#ffffff',
  text: '#0f172a',
  textMuted: '#475569',
} as const;

interface SectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function Section({ title, subtitle, children }: SectionProps) {
  return (
    <Box component="section" sx={{ mb: { xs: 6, md: 8 } }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: colors.brand,
          letterSpacing: '0.01em',
          mb: 1.2,
          fontSize: { xs: '1.6rem', sm: '2rem' },
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            color: colors.textMuted,
            mb: 2.5,
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.7,
          }}
        >
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ borderColor: colors.border, mb: 3.2 }} />
      {children}
    </Box>
  );
}

interface CapabilityCardProps {
  title: string;
  route: string;
  body: string;
  accent?: string;
}

function CapabilityCard({ title, route, body, accent }: CapabilityCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${accent ?? colors.brand}`,
        borderRadius: 2,
        p: { xs: 2.5, md: 3.2 },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          color: colors.text,
          mb: 0.7,
          fontSize: { xs: '1.12rem', md: '1.25rem' },
        }}
      >
        {title}
      </Typography>
      <Chip
        size="small"
        label={route}
        sx={{
          mb: 1.5,
          backgroundColor: colors.surface3,
          color: colors.brandLight,
          fontFamily: 'monospace',
          fontSize: '0.76rem',
        }}
      />
      <Typography
        sx={{
          color: colors.textMuted,
          lineHeight: 1.75,
          fontSize: { xs: '0.97rem', md: '1.03rem' },
        }}
      >
        {body}
      </Typography>
    </Box>
  );
}

interface DetailCardProps {
  title: string;
  body: string;
  bullets?: readonly string[];
}

function DetailCard({ title, body, bullets }: DetailCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        p: { xs: 2.3, md: 2.8 },
      }}
    >
      <Typography
        sx={{
          color: colors.text,
          fontWeight: 800,
          mb: 0.8,
          fontSize: { xs: '1rem', md: '1.08rem' },
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: colors.textMuted,
          lineHeight: 1.7,
          fontSize: { xs: '0.95rem', md: '1rem' },
          mb: bullets?.length ? 1.4 : 0,
        }}
      >
        {body}
      </Typography>
      {bullets?.length ? (
        <Box component="ul" sx={{ m: 0, pl: 2.2 }}>
          {bullets.map((bullet) => (
            <Typography
              component="li"
              key={bullet}
              sx={{
                color: colors.textMuted,
                lineHeight: 1.65,
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                mb: 0.6,
              }}
            >
              {bullet}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

interface FieldSpec {
  name: string;
  type: string;
  description: string;
}

interface FieldReferenceCardProps {
  entity: string;
  accent?: string;
  fields: readonly FieldSpec[];
}

function FieldReferenceCard({
  entity,
  accent,
  fields,
}: FieldReferenceCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${accent ?? colors.brand}`,
        borderRadius: 2,
        p: { xs: 2.3, md: 2.8 },
      }}
    >
      <Typography
        sx={{
          color: colors.text,
          fontWeight: 800,
          mb: 1.3,
          fontSize: { xs: '1rem', md: '1.08rem' },
        }}
      >
        {entity}
      </Typography>
      {fields.map((field) => (
        <Box
          key={field.name}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '130px 95px 1fr' },
            gap: { xs: 0.4, sm: 1 },
            py: 1.05,
            borderBottom: `1px solid ${colors.border}`,
            '&:last-child': { borderBottom: 'none', pb: 0 },
          }}
        >
          <Typography
            sx={{
              color: colors.brandLight,
              fontFamily: 'monospace',
              fontWeight: 700,
              fontSize: '0.84rem',
            }}
          >
            {field.name}
          </Typography>
          <Typography
            sx={{
              color: accent ?? colors.pubAccent,
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              fontWeight: 600,
            }}
          >
            {field.type}
          </Typography>
          <Typography
            sx={{
              color: colors.textMuted,
              fontSize: { xs: '0.88rem', md: '0.92rem' },
              lineHeight: 1.55,
            }}
          >
            {field.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const coreCapabilities = [
  {
    title: 'Elliot',
    route: '/capabilities/elliot',
    body: 'AI workspace for query-driven analysis with chat, model selection, and intelligence dashboard modules.',
    accent: '#818cf8',
  },
  {
    title: 'Network Intelligence',
    route: '/capabilities/network-intelligence',
    body: 'Unified graph workspace that seeds and expands publication-centered networks across multiple modes.',
    accent: '#6366f1',
  },
  {
    title: 'MIL-CIV Fusion',
    route: '/capabilities/mil-civ-fusion',
    body: 'Focused workflow for Military-Civil Fusion exploration with tree and force-graph analysis.',
    accent: '#10b981',
  },
  {
    title: 'Mariana',
    route: '/capabilities/mariana',
    body: 'Embedded capability experience delivered through the internal Mariana iframe endpoint.',
    accent: '#06b6d4',
  },
  {
    title: 'DSTKL Tracker',
    route: '/capabilities/dstkl-tracker',
    body: 'Tabbed view that exposes both the DSTKL Table and the Global Research Tracker interfaces.',
    accent: '#f59e0b',
  },
  {
    title: 'PLA Procurement Vetting',
    route: '/capabilities/export-control-vetting',
    body: 'Procurement-focused dashboard for PLA project-code detection, supplier screening, and critical-technology exposure review.',
    accent: '#38bdf8',
  },
  {
    title: 'PLA Yellow Pages',
    route: '/capabilities/pla-yellow-pages',
    body: 'Organization-focused capability for institution discovery, classification, and intelligence pivots.',
    accent: '#ec4899',
  },
  {
    title: 'Mentions',
    route: '/capabilities/latest-mentions',
    body: 'Mentions intelligence dashboard with timeline-driven entries and filterable categories/risk levels.',
    accent: '#ef4444',
  },
  {
    title: 'Orbital Nexus',
    route: '/capabilities/orbital-nexus',
    body: "Interactive geospatial intelligence platform that maps and explores the relationships between China's space ecosystem, research projects, researchers, and affiliated PLA entities.",
    accent: '#0ea5e9',
  },
  {
    title: 'Defense Technology Intelligence',
    route: '/capabilities/defense-technology-intelligence',
    body: 'Fuses Publications/Research and PLA Contracts/Procurement data into a single evidence-backed intelligence graph spanning Organizations, Research, Technology, PLA, Procurement, and Suppliers.',
    accent: '#dc2626',
  },
  {
    title: 'MCP Server',
    route: '/tools/mcp-explorer',
    body: 'Model Context Protocol server providing programmatic API access to publications search and PLA Contracts/procurement search for AI-driven integration and automation.',
    accent: '#a855f7',
  },
].filter((capability) => isCapabilityAvailable(capability.route));

const searchSurfaces = [
  {
    name: 'Publications',
    route: '/search/publications',
    bestFor:
      'Source-level research outputs, citations, publication metadata, and trend discovery.',
  },
  {
    name: 'People',
    route: '/search/people',
    bestFor:
      'Researcher profile analysis, affiliation tracing, and author-level investigation.',
  },
  {
    name: 'Organizations',
    route: '/search/organizations',
    bestFor:
      'Institution discovery, organization intelligence, and network anchor validation.',
  },
  {
    name: 'Technologies',
    route: '/search/technologies',
    bestFor:
      'Technology-signal lookups, capability tracking, and topic-focused intelligence pivots.',
  },
] as const;

const fieldLevelReferences = [
  {
    entity: 'Publication',
    accent: '#f59e0b',
    fields: [
      {
        name: 'doi',
        type: 'string',
        description: 'Canonical publication identifier.',
      },
      { name: 'title', type: 'string', description: 'Full publication title.' },
      {
        name: 'year',
        type: 'number',
        description: 'Publication year used for chronology and filtering.',
      },
      {
        name: 'risk',
        type: 'number',
        description: 'Dual-use risk score on a 0-100 scale.',
      },
      {
        name: 'topAuthors',
        type: 'string[]',
        description: 'Primary contributing authors.',
      },
      {
        name: 'topAffiliations',
        type: 'string[]',
        description: 'Lead associated institutions.',
      },
      {
        name: 'topKeywords',
        type: 'string[]',
        description: 'Dominant abstract/topic keywords.',
      },
    ],
  },
  {
    entity: 'People / Author',
    accent: '#10b981',
    fields: [
      {
        name: 'id',
        type: 'string',
        description: 'Platform author identifier.',
      },
      { name: 'name', type: 'string', description: 'Researcher display name.' },
      {
        name: 'email',
        type: 'string',
        description: 'Public or institutional contact email when available.',
      },
      {
        name: 'affiliations',
        type: 'string[]',
        description: 'Known institution affiliations.',
      },
      {
        name: 'publicationCount',
        type: 'number',
        description: 'Indexed publication total.',
      },
    ],
  },
  {
    entity: 'Organization / Affiliation',
    accent: '#6366f1',
    fields: [
      {
        name: 'id',
        type: 'string',
        description: 'Platform affiliation identifier.',
      },
      {
        name: 'name',
        type: 'string',
        description: 'Organization display name.',
      },
      {
        name: 'country',
        type: 'string',
        description: 'Country or ISO country code.',
      },
      {
        name: 'type',
        type: 'string',
        description:
          'Entity type (for example university, company, government).',
      },
      {
        name: 'keywords',
        type: 'string[]',
        description: 'Core research and technology focus areas.',
      },
    ],
  },
  {
    entity: 'Technology / Keyword',
    accent: '#06b6d4',
    fields: [
      {
        name: 'keyword',
        type: 'string',
        description: 'Primary technology term.',
      },
      {
        name: 'publicationCount',
        type: 'number',
        description: 'Number of matching publications.',
      },
      {
        name: 'relatedKeywords',
        type: 'string[]',
        description: 'Frequent co-occurring terms used for pivots.',
      },
    ],
  },
  {
    entity: 'Award / Funding',
    accent: '#ec4899',
    fields: [
      {
        name: 'funder_award',
        type: 'string',
        description: 'Award identifier from the funder record.',
      },
      {
        name: 'funder_name',
        type: 'string',
        description: 'Primary funding agency name.',
      },
      { name: 'funders', type: 'string[]', description: 'All linked funders.' },
      {
        name: 'titles',
        type: 'string[]',
        description: 'Grant or project titles.',
      },
      {
        name: 'authors',
        type: 'string[]',
        description: 'Recipient researchers.',
      },
      {
        name: 'affiliations',
        type: 'string[]',
        description: 'Linked recipient organizations.',
      },
      {
        name: 'years',
        type: 'string[]',
        description: 'Associated award years.',
      },
      {
        name: 'publication_count',
        type: 'number',
        description: 'Count of linked publications.',
      },
      {
        name: 'dois',
        type: 'string[]',
        description: 'Linked publication DOI values.',
      },
      {
        name: 'keywords',
        type: 'string[]',
        description: 'Award-linked research topics.',
      },
    ],
  },
] as const;

const workflowCards = [
  {
    title: '1) Start with an objective',
    body: 'Define what you need to answer before selecting a search surface or capability route.',
    bullets: [
      'Choose a person, organization, technology, or publication-centered question.',
      'Set a narrow first pass to reduce noise before broadening scope.',
    ],
  },
  {
    title: '2) Use search to establish ground truth',
    body: 'Run a targeted query in the most appropriate search surface to build an initial evidence set.',
    bullets: [
      'Cross-check names, aliases, and institution variants.',
      'Save high-signal entities to branch into capability workflows.',
    ],
  },
  {
    title: '3) Pivot into capabilities',
    body: 'Move from raw discovery into relationship, timeline, or network analysis using capability modules.',
    bullets: [
      'Use Network Intelligence or MIL-CIV Fusion when the question is relationship-heavy.',
      'Use Mariana or tracker surfaces when specialized filters are required.',
    ],
  },
  {
    title: '4) Validate and operationalize',
    body: 'Reconcile key findings across multiple modules, then package output for sharing and follow-on analysis.',
    bullets: [
      'Confirm high-impact claims in at least one additional surface.',
      'Document route links used so teammates can reproduce the workflow.',
    ],
  },
] as const;

const operationsCards = [
  {
    title: 'Access and navigation',
    body: 'Most workflows begin from Home, Search, or Capabilities. Documentation is intended as the central route index and process guide.',
  },
  {
    title: 'Capability coverage model',
    body: 'Core capabilities listed above are actively surfaced in primary navigation, while extended routes remain available for teams using legacy or specialized flows.',
  },
  {
    title: 'Evidence hygiene',
    body: 'When moving quickly between modules, preserve route paths and query terms so findings stay reproducible and auditable.',
  },
] as const;

export default function DocumentationPage() {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const bg = prefersDark ? colors.bg : lightColors.bg;
  const surface = prefersDark ? colors.surface2 : lightColors.surface;
  const textPrimary = prefersDark ? colors.text : lightColors.text;
  const textSecondary = prefersDark ? colors.textMuted : lightColors.textMuted;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: bg,
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 5, md: 7 },
      }}
    >
      <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
        <Box
          sx={{
            backgroundColor: surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            p: { xs: 3, md: 4.5 },
            mb: { xs: 5, md: 6 },
          }}
        >
          <Typography
            sx={{
              color: colors.brand,
              fontWeight: 800,
              letterSpacing: '0.13em',
              fontSize: '0.76rem',
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            Data Abyss Documentation
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              color: textPrimary,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              mb: 1.4,
              fontSize: { xs: '2.1rem', md: '3rem' },
            }}
          >
            Platform Guide
          </Typography>
          <Typography
            sx={{
              color: textSecondary,
              maxWidth: 940,
              lineHeight: 1.75,
              fontSize: { xs: '1rem', md: '1.12rem' },
            }}
          >
            This guide covers where key workflows live, when to use each
            surface, and how to run investigations from first query to validated
            output.
          </Typography>
        </Box>

        <Section
          title="Core Capabilities"
          subtitle="Primary capabilities exposed in the Capabilities navigation menu."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2.2,
            }}
          >
            {coreCapabilities.map((capability) => (
              <CapabilityCard key={capability.route} {...capability} />
            ))}
          </Box>
        </Section>

        <Section
          title="Primary Search Surfaces"
          subtitle="Main entry points for entity search workflows and what each one is best for."
        >
          <Box
            sx={{
              backgroundColor: surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              p: { xs: 2.5, md: 3 },
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {searchSurfaces.map((surfaceEntry) => (
              <Box
                key={surfaceEntry.route}
                sx={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 1.5,
                  px: 2,
                  py: 1.7,
                  backgroundColor: colors.surface,
                }}
              >
                <Typography
                  sx={{
                    color: colors.text,
                    fontWeight: 700,
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    mb: 0.4,
                  }}
                >
                  {surfaceEntry.name}
                </Typography>
                <Typography
                  sx={{
                    color: colors.brandLight,
                    fontFamily: 'monospace',
                    fontSize: '0.83rem',
                  }}
                >
                  {surfaceEntry.route}
                </Typography>
                <Typography
                  sx={{
                    color: colors.textMuted,
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                    lineHeight: 1.65,
                    mt: 1,
                  }}
                >
                  {surfaceEntry.bestFor}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>

        <Section
          title="Field-Level Reference"
          subtitle="Detailed field-level material for the core entities used throughout search, dossiers, and capability workflows."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2.2,
            }}
          >
            {fieldLevelReferences.map((entry) => (
              <FieldReferenceCard
                key={entry.entity}
                entity={entry.entity}
                accent={entry.accent}
                fields={entry.fields}
              />
            ))}
          </Box>
        </Section>

        <Section
          title="Recommended Investigation Workflow"
          subtitle="A reusable process for turning exploration into repeatable intelligence output."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2.2,
            }}
          >
            {workflowCards.map((card) => (
              <DetailCard key={card.title} {...card} />
            ))}
          </Box>
        </Section>

        <Section
          title="Operational Guidance"
          subtitle="Working principles that help keep findings consistent, traceable, and production-ready."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2.2,
            }}
          >
            {operationsCards.map((card) => (
              <DetailCard key={card.title} {...card} />
            ))}
          </Box>
        </Section>
      </Box>
    </Box>
  );
}
